import React, { useMemo } from 'react';
import { Typography } from '@mui/material';

import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import { MenuItem } from 'ui/components/Menu';
import { IconNames } from 'ui/theme';
import { PermissionType } from 'services/permissions';

interface TransferOrderTitleBarProps {
  orderNumber: string;
  isGenerating?: boolean;
  isNewTransferOrder: boolean;
  isProcessing?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
  onSave: () => void;
  onSaveAndClose: () => void;
  onIssue: () => void;
  onQuickFulfill: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const TransferOrderTitleBar: React.FC<TransferOrderTitleBarProps> = ({
  orderNumber,
  isGenerating,
  isNewTransferOrder,
  isProcessing,
  canCreate = true,
  canDelete = false,
  onSave,
  onSaveAndClose,
  onIssue,
  onQuickFulfill,
  onDelete,
  onClose,
}) => {
  const menuOptions = useMemo<MenuItem[]>(() => {
    const options: MenuItem[] = [];
    if (!isNewTransferOrder && canDelete) {
      options.push({
        icon: IconNames.TrashCan,
        label: 'Delete',
        dataQa: 'transfer-order-delete',
        permissions: [PermissionType.TransferOrdersDelete],
        onOptionClick: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onDelete();
        },
      });
    }
    return options;
  }, [isNewTransferOrder, canDelete, onDelete]);

  return (
    <FBOTitleBar
      title={
        <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
          {isGenerating ? 'Generating...' : orderNumber}
        </Typography>
      }
    >
      <>
        <>
          {/*
            NOTE: These buttons are foundation-only implementations for Transfer Order form structure.
            Full functionality requires future API development:
            - Quick Fulfill: Requires pick/fulfill APIs and workflow implementation
            - Issue: Requires transfer order issue/status change APIs
            - Save: Currently creates draft transfer orders (functional)
            - Save & Close: Currently creates draft transfer orders and closes form (functional)

            The Save and Save & Close buttons are the only fully functional actions in this iteration.
            Issue and Quick Fulfill buttons will be enhanced when supporting APIs are available.
          */}
          <FBOButton
            sx={{ marginRight: '8px' }}
            variant="secondary"
            color="positive"
            size="medium"
            onClick={onQuickFulfill}
            disabled={isProcessing || !canCreate}
            loading={isProcessing}
            permissions={[PermissionType.TransferOrdersCreate]}
            data-qa="transfer-order-quick-fulfill"
          >
            Quick Fulfill
          </FBOButton>

          <FBOButton
            sx={{ marginRight: '8px' }}
            variant="secondary"
            color="positive"
            size="medium"
            onClick={onIssue}
            disabled={isProcessing || !canCreate}
            loading={isProcessing}
            permissions={[PermissionType.TransferOrdersCreate]}
            data-qa="transfer-order-issue"
          >
            Issue
          </FBOButton>

          <FBOButton
            sx={{ marginRight: '8px' }}
            variant="secondary"
            color="positive"
            size="medium"
            onClick={onSave}
            disabled={isProcessing || !canCreate}
            loading={isProcessing}
            permissions={[PermissionType.TransferOrdersCreate]}
            data-qa="transfer-order-save"
          >
            Save
          </FBOButton>

          <FBOButton
            sx={{ marginRight: '8px' }}
            variant="secondary"
            color="positive"
            size="medium"
            onClick={onSaveAndClose}
            disabled={isProcessing || !canCreate}
            loading={isProcessing}
            permissions={[PermissionType.TransferOrdersCreate]}
            data-qa="transfer-order-save-close"
          >
            Save & Close
          </FBOButton>
        </>

        {menuOptions.length > 0 && (
          <FBOMenuButton
            variant="tertiary"
            items={menuOptions}
            data-qa="transfer-order-three-dot-menu"
          />
        )}

        <FBOButton
          variant="tertiary"
          color="neutral"
          size="medium"
          icon={IconNames.FBOClose}
          aria-label="Close"
          data-qa="transfer-order-close"
          onClick={onClose}
        />
      </>
    </FBOTitleBar>
  );
};

export default TransferOrderTitleBar;
